/**
 * Câblage de la couche métier pour l'interface web.
 *
 * C'est ici, et seulement ici, que le front rencontre `@dker/core`. Les pages
 * et les hooks appellent des services ; ils ne construisent ni dépôt, ni
 * contexte d'exécution à la main.
 *
 * Le client Supabase du navigateur porte déjà la session de l'utilisateur :
 * les policies RLS s'appliquent donc avec son identité, sans qu'on ait à
 * transmettre le tenant. C'est exactement ce que le port `DbClientFactory`
 * exige — et ce qu'un client `service_role` violerait.
 */
import { useCallback } from "react";
import {
  confirmable,
  createAlertServices,
  createCatalogServices,
  createConfirmationServices,
  createDocumentServices,
  createClientServices,
  createDashboardServices,
  createPhotoServices,
  createSiteDocumentServices,
  createEquipmentServices,
  createSubcontractingServices,
  createSiteLeadServices,
  createExportServices,
  createTerminationServices,
  createPortalServices,
  createAgentGatewayServices,
  confirmableWrite,
  expenseSummary,
  paymentSummary,
  recordPaymentInput,
  saveExpenseInput,
  createSupportServices,
  createPilotageServices,
  createDeadlineServices,
  createAgendaServices,
  createCertificationServices,
  createAttendanceServices,
  createSafetyServices,
  createEveningServices,
  createPlanningServices,
  createExpenseServices,
  createFieldServices,
  createMaterialOrdersServices,
  createOrderingServices,
  createPurchasesServices,
  createSupplierServices,
  createTeamServices,
  createTenantConfigServices,
  createTenantServices,
  createTreasuryServices,
  createInvoiceServices,
  createProjectServices,
  createQuoteServices,
} from "@dker/core";
import {
  billAndSendInput,
  followUpQuoteInput,
  orderStockItemInput,
  sendInvoiceInput,
  sendMaterialOrderInput,
  sendPaymentReminderInput,
  sendQuoteInput,
  submitChorusProInput,
} from "@dker/core";
import type { ExecutionContext, ServiceDeps } from "@dker/core";
import {
  createSupabaseAudit,
  createSupabaseAlertRepository,
  createSupabaseCatalogRepository,
  createSupabaseClientRepository,
  createSupabaseDashboardRepository,
  createSupabaseExpenseRepository,
  createSupabaseTenantConfigRepository,
  createSupabaseTenantRepository,
  createSupabaseTreasuryRepository,
  createSupabaseInvoiceRepository,
  createSupabaseProjectRepository,
  createSupabasePhotoRepository,
  createSupabaseSiteDocumentRepository,
  createSupabaseEquipmentRepository,
  createSupabaseSubcontractingRepository,
  createSupabaseSiteLeadRepository,
  createSupabaseExportRepository,
  createSupabaseTerminationRepository,
  createSupabasePortalRepository,
  createSupabaseAgentGateway,
  createSupabaseSupportVisitRepository,
  createSupabaseConfirmationRepository,
  createSupabaseConfirmations,
  createSupabaseDocumentPort,
  createSupabaseFieldRepository,
  createSupabaseMaterialOrdersRepository,
  createSupabasePilotageRepository,
  createSupabaseDeadlineRepository,
  createSupabaseAppointmentRepository,
  createSupabaseCertificationRepository,
  createSupabaseAttendanceRepository,
  createSupabaseSafetyRepository,
  createSupabaseEveningRepository,
  createSupabasePlanningRepository,
  createSupabasePurchasesRepository,
  createSupabaseQuoteRepository,
  createSupabaseSupplierRepository,
  createSupabaseTeamRepository,
} from "@dker/data";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/authStore";
import { captureException } from "@/lib/observability";

const clientFor = () => supabase;

export const quoteServices = createQuoteServices(createSupabaseQuoteRepository(clientFor));
export const projectServices = createProjectServices(createSupabaseProjectRepository(clientFor));

// Les photos de chantier sont un domaine à part : elles ont leur propre
// échéance de conservation, que le chantier ne porte pas.
export const photoServices = createPhotoServices(createSupabasePhotoRepository(clientFor));

// Plans et cahiers des charges d'un chantier : le bureau dépose, le terrain
// lit ce qu'on lui partage. La base décide de qui voit quoi.
export const siteDocumentServices = createSiteDocumentServices(
  createSupabaseSiteDocumentRepository(clientFor),
);

// Ce que le chef de chantier voit de ses chantiers : l'équipe, la semaine, les
// remontées. Trois projections étroites, bornées par la base.
// Le matériel : où est chaque outil, chaque machine. La base décide de qui
// voit et déplace quoi ; les mouvements passent par ses deux fonctions.
export const equipmentServices = createEquipmentServices(createSupabaseEquipmentRepository(clientFor));

export const siteLeadServices = createSiteLeadServices(createSupabaseSiteLeadRepository(clientFor));
// L'export des données : tout ce que le patron emporte en partant. Le noyau ne
// connaît ni `TextEncoder` ni `crypto` ; le navigateur les lui prête.
const textEncoder = new TextEncoder();
export const exportServices = createExportServices(createSupabaseExportRepository(clientFor), {
  encode: (text) => textEncoder.encode(text),
  sha256: async (data) => {
    // Les octets viennent de `TextEncoder` ou du Storage : jamais partagés.
    const digest = await crypto.subtle.digest("SHA-256", data as Uint8Array<ArrayBuffer>);
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  },
});

// La résiliation posée par le staff : l'application l'annonce, la base l'applique.
export const terminationServices = createTerminationServices(
  createSupabaseTerminationRepository(clientFor),
);

export const portalServices = createPortalServices(
  createSupabasePortalRepository(clientFor),
);

// Qui du support DKER est entré chez le patron, quand et pourquoi.
export const supportServices = createSupportServices(
  createSupabaseSupportVisitRepository(clientFor),
);

export const clientServices = createClientServices(
  createSupabaseClientRepository(clientFor),
);

export const catalogServices = createCatalogServices(
  createSupabaseCatalogRepository(clientFor),
);

const invoiceRepository = createSupabaseInvoiceRepository(clientFor);
const quoteRepository = createSupabaseQuoteRepository(clientFor);
const projectRepository = createSupabaseProjectRepository(clientFor);

export const invoiceServices = createInvoiceServices(invoiceRepository);

// Le tableau de bord ne requête presque rien lui-même : il compose les dépôts
// des autres domaines, ce qui garantit qu'un même montant y est identique
// partout dans l'application.
export const dashboardServices = createDashboardServices({
  dashboard: createSupabaseDashboardRepository(clientFor),
  invoices: invoiceRepository,
  quotes: quoteRepository,
  projects: projectRepository,
});

// ─── Documents et transmission ─────────────────────────────────────────────
// Les quatre Edge Functions, enfin atteignables — et seulement à travers un
// service qui déclare leur niveau. Générer un PDF est réversible ; déposer sur
// Chorus Pro ou relancer un client ne l'est pas, et le socle l'impose.
const documentPort = createSupabaseDocumentPort(clientFor);
const materialOrdersRepository = createSupabaseMaterialOrdersRepository(clientFor);

export const documentServices = createDocumentServices(documentPort);

/**
 * Commander un article d'un seul accord — lot 3 du plan UI/UX. Il compose le
 * dépôt des commandes et le port documentaire, et ne vit donc ni dans l'un ni
 * dans l'autre.
 */
export const orderingServices = createOrderingServices({
  orders: materialOrdersRepository,
  documents: documentPort,
});

export const expenseServices = createExpenseServices(
  createSupabaseExpenseRepository(clientFor),
);

/**
 * Les actions à effet externe que l'application sait faire confirmer.
 *
 * Registre fermé : ce qui n'y figure pas ne peut pas être préparé, donc ne
 * peut pas être confirmé, donc ne s'exécute pas. Ajouter un verbe N3 sans
 * l'inscrire ici le rend inatteignable — l'oubli échoue du bon côté.
 */
export const confirmationServices = createConfirmationServices(
  createSupabaseConfirmationRepository(clientFor),
  [
    confirmable(documentServices.envoyerDevis, sendQuoteInput),
    confirmable(documentServices.envoyerFacture, sendInvoiceInput),
    confirmable(documentServices.relancerFacture, sendPaymentReminderInput),
    confirmable(documentServices.transmettreChorusPro, submitChorusProInput),
    confirmable(documentServices.envoyerCommande, sendMaterialOrderInput),
    confirmable(documentServices.relancerDevis, followUpQuoteInput),
    confirmable(documentServices.facturerEtEnvoyer, billAndSendInput),
    confirmable(orderingServices.commanderMateriel, orderStockItemInput),
    // L'argent que l'agent prépare : N2 à l'écran, mais soumis au oui du
    // patron quand c'est l'agent qui le propose (décision du 18 septembre).
    // Même registre que l'API, pour que ce qu'une porte prépare, l'autre sache
    // l'exécuter.
    confirmableWrite(invoiceServices.enregistrerPaiement, recordPaymentInput, paymentSummary),
    confirmableWrite(expenseServices.enregistrerDepense, saveExpenseInput, expenseSummary),
  ],
);

/** L'agent du patron, servi par l'Edge Function `agent`. */
export const agentServices = createAgentGatewayServices(createSupabaseAgentGateway(clientFor));

export const serviceDeps: ServiceDeps = {
  audit: createSupabaseAudit(clientFor, (error) => {
    // Perdre une ligne de journal ne doit pas faire échouer l'action du
    // patron, mais un journal silencieusement vide passerait pour un journal
    // propre — donc on le signale.
    captureException(error);
  }),
  confirmations: createSupabaseConfirmations(clientFor),
};

/**
 * Fabrique un contexte d'exécution pour l'appel en cours.
 *
 * Une fabrique plutôt qu'une valeur mémorisée : le `requestId` doit être
 * propre à chaque action, sinon toutes les écritures d'une session partagent
 * la même corrélation et le journal devient illisible.
 */
export function useExecutionContext(): () => ExecutionContext {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);

  return useCallback(() => {
    if (!user || !tenant) {
      throw new Error("Contexte d'exécution demandé hors session authentifiée");
    }
    return {
      tenantId: tenant.id,
      actorId: user.id,
      actorType: "human_ui",
      role: user.role,
      channel: "web",
      requestId: crypto.randomUUID(),
      fromUntrustedContent: false,
    };
  }, [user, tenant]);
}

export const treasuryServices = createTreasuryServices(
  createSupabaseTreasuryRepository(clientFor),
);

export const alertServices = createAlertServices(
  createSupabaseAlertRepository(clientFor),
);

export const tenantServices = createTenantServices(
  createSupabaseTenantRepository(clientFor),
);

/**
 * Configuration tenant — la cadence de relance et l'identité visuelle.
 *
 * Elle gouverne à la fois le ton proposé par l'écran de facture et les
 * paliers que la fonction planifiée fait franchir. C'est le point : les deux
 * lisent la même ligne, là où ils portaient chacun leurs seuils en dur.
 */
const tenantConfigRepository = createSupabaseTenantConfigRepository(clientFor);

export const tenantConfigServices = createTenantConfigServices(tenantConfigRepository);

/**
 * Équipe et pointage.
 *
 * Le coût horaire n'arrive ici que si la session est celle du patron : la
 * policy `patron_only` filtre la ligne, et l'absence se lit `null` — jamais
 * zéro, sans quoi une heure deviendrait gratuite et la marge, flatteuse.
 */
const teamRepository = createSupabaseTeamRepository(clientFor);

export const teamServices = createTeamServices(teamRepository);

/**
 * Le terrain : missions, affectations, signalements.
 *
 * Les missions ne passent pas par `projects` — l'ouvrier n'y a aucun droit.
 * C'est `my_missions()` qui projette en base ce qui a le droit d'en sortir,
 * la RLS ne sachant pas filtrer des colonnes.
 */
export const fieldServices = createFieldServices(
  createSupabaseFieldRepository(clientFor),
);

/**
 * Réception d'e-factures : les factures fournisseurs, rapatriées depuis
 * SUPER PDP par l'Edge Function `sync-received-invoices`, jamais lues ici.
 */
export const purchasesServices = createPurchasesServices(
  createSupabasePurchasesRepository(clientFor),
);

export const supplierServices = createSupplierServices(
  createSupabaseSupplierRepository(clientFor),
);

/** Le carnet des sous-traitants, et ce que chaque chantier leur confie. */
export const subcontractingServices = createSubcontractingServices(
  createSupabaseSubcontractingRepository(clientFor),
);

/**
 * Commandes fournisseurs : le panier catalogue réparti par fournisseur, et
 * sa réception — voir `create_material_orders_from_basket` et
 * `receive_material_order` côté base.
 */
export const materialOrdersServices = createMaterialOrdersServices(materialOrdersRepository);

/**
 * Le planning — le seul module qui regarde devant.
 *
 * Il ne possède presque rien : les absences, et c'est tout. L'équipe, les
 * affectations et les chantiers se lisent dans leurs dépôts respectifs, ceux-là
 * mêmes que servent les autres écrans. C'est ce qui garantit qu'une affectation
 * posée depuis la fiche d'un chantier apparaît dans la grille sans qu'une
 * seconde requête ait à en dire autre chose.
 */
export const planningServices = createPlanningServices({
  absences: createSupabasePlanningRepository(clientFor),
  team: createSupabaseTeamRepository(clientFor),
  field: createSupabaseFieldRepository(clientFor),
  projects: projectRepository,
});

/**
 * Les échéances administratives — décennale, TVA, RGE, retenues de garantie.
 *
 * Le seul domaine dont les dates ne sont celles d'aucun document. Il ne lit
 * rien d'autre : une échéance ne se déduit de rien, elle se saisit.
 */
export const deadlineServices = createDeadlineServices(
  createSupabaseDeadlineRepository(clientFor),
);

/**
 * L'agenda du jour — les rendez-vous à heure fixe.
 *
 * Distinct des échéances : une échéance a une date, un rendez-vous une heure,
 * un client et un lieu.
 */
export const agendaServices = createAgendaServices(
  createSupabaseAppointmentRepository(clientFor),
);

/**
 * Les papiers des équipes — CACES, visite médicale, SST.
 *
 * Distincts des échéances administratives : un papier appartient à une
 * personne et se renouvelle par une session qu'il faut réserver à l'avance.
 */
export const certificationServices = createCertificationServices(
  createSupabaseCertificationRepository(clientFor),
);

/**
 * La présence sur chantier — l'arrivée et le départ pointés par le compagnon.
 *
 * Une preuve de présence, pas une heure payée : le bureau la reprend dans le
 * pointage.
 */
export const attendanceServices = createAttendanceServices(
  createSupabaseAttendanceRepository(clientFor),
);

/**
 * Les quarts d'heure sécurité — la trace, et le rappel à l'encadrement.
 */
export const safetyServices = createSafetyServices(createSupabaseSafetyRepository(clientFor));

/**
 * La file du soir — ce qui attend le patron en fin de journée.
 *
 * Elle ne possède rien : quatre signaux assemblés depuis les dépôts qui les
 * servent déjà ailleurs. Une facture échue affichée dans la file est celle de
 * l'écran des factures, au centime et au jour près — et c'est ce qui garantit
 * qu'une relance proposée le soir n'est pas une relance déjà partie le matin.
 */
export const eveningServices = createEveningServices(
  createSupabaseEveningRepository(clientFor, {
    invoices: invoiceRepository,
    projects: projectRepository,
    quotes: quoteRepository,
    config: tenantConfigRepository,
  }),
);

/**
 * Le pilotage — la rentabilité par chantier et le carnet de commandes.
 *
 * Il reçoit `projectRepository`, le même que sert l'écran Chantiers, et c'est
 * ce qui garantit qu'une marge affichée sur le tableau de bord est la marge
 * affichée sur la fiche. Le reste du tableau de bord — trésorerie, encours,
 * marge du mois, charge — se lit par les services qui existent déjà : en
 * refaire une copie ici ferait diverger un même montant d'un écran à l'autre.
 */
export const pilotageServices = createPilotageServices(
  createSupabasePilotageRepository(clientFor, projectRepository, teamRepository),
);
